const INGEST_URL = import.meta.env.VITE_LOG_INGEST_URL as string | undefined
const APP_VERSION = import.meta.env.VITE_APP_VERSION as string | undefined
const SOURCE = 'booky'
const MAX_BREADCRUMBS = 50

type Severity = 'DEFAULT' | 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL'

interface Breadcrumb {
  timestamp: string
  category: string
  message: string
  data?: Record<string, unknown>
}

interface LogEvent {
  source: string
  eventType: string
  sessionId: string
  userId?: string
  appVersion?: string
  route: string
  url: string
  userAgent: string
  message: string
  errorName?: string
  stack?: string
  severity: Severity
  metadata?: Record<string, unknown>
  breadcrumbs: Breadcrumb[]
  occurredAt: string
}

const sessionId = crypto.randomUUID()
const breadcrumbs: Breadcrumb[] = []
let currentUserId: string | undefined

export function setLoggerUserId(id: string | undefined) {
  currentUserId = id
}

export function addBreadcrumb(category: string, message: string, data?: Record<string, unknown>) {
  breadcrumbs.push({
    timestamp: new Date().toISOString(),
    category,
    message,
    data,
  })
  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.shift()
  }
}

export function sendEvent(
  eventType: string,
  message: string,
  severity: Severity = 'ERROR',
  extras?: {
    errorName?: string
    stack?: string
    metadata?: Record<string, unknown>
  },
) {
  if (!INGEST_URL) return

  const event: LogEvent = {
    source: SOURCE,
    eventType,
    sessionId,
    userId: currentUserId,
    appVersion: APP_VERSION,
    route: window.location.pathname,
    url: window.location.href,
    userAgent: navigator.userAgent,
    message,
    errorName: extras?.errorName,
    stack: extras?.stack,
    severity,
    metadata: extras?.metadata,
    breadcrumbs: [...breadcrumbs],
    occurredAt: new Date().toISOString(),
  }

  const body = JSON.stringify(event)

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' })
    navigator.sendBeacon(`${INGEST_URL}/v1/client-events`, blob)
  } else {
    fetch(`${INGEST_URL}/v1/client-events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // intentionally silenced — logging must never break the app
    })
  }
}

export function initLogger() {
  window.addEventListener('error', (event) => {
    sendEvent('runtime.error', event.message || 'Uncaught error', 'ERROR', {
      errorName: event.error instanceof Error ? event.error.name : 'Error',
      stack: event.error instanceof Error ? event.error.stack : undefined,
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection'

    sendEvent('runtime.unhandled_rejection', message, 'ERROR', {
      errorName: reason instanceof Error ? reason.name : undefined,
      stack: reason instanceof Error ? reason.stack : undefined,
    })
  })

  addBreadcrumb('app', 'App initialised')
}
